/**
 * US Formatting Utilities
 * 
 * American formatting standards for dates, currency, and numbers
 */

/**
 * Format a date to US format (MM/DD/YYYY)
 */
export const formatUSDate = (date: Date | string | null | undefined): string => {
  if (!date) return '-';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) return '-';
  
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  
  return `${month}/${day}/${year}`;
};

/**
 * Format a date and time to US format (MM/DD/YYYY HH:MM AM/PM)
 */
export const formatUSDateTime = (date: Date | string | null | undefined): string => {
  if (!date) return '-';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) return '-';
  
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  
  return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
};

/**
 * Format currency as USD ($)
 */
export const formatUSD = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined) return '-';
  
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(num)) return '-';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

/**
 * Format currency as USD without decimal places (for whole dollars)
 */
export const formatUSDWhole = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined) return '-';
  
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(num)) return '-';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

/**
 * Format a number with US thousands separator
 */
export const formatUSNumber = (num: number | string | null | undefined): string => {
  if (num === null || num === undefined) return '-';
  
  const n = typeof num === 'string' ? parseFloat(num) : num;
  
  if (isNaN(n)) return '-';
  
  return new Intl.NumberFormat('en-US').format(n);
};

/**
 * Format a US phone number
 */
export const formatUSPhone = (phone: string | null | undefined): string => {
  if (!phone) return '-';
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Format as US phone number
  if (digits.startsWith('1') && digits.length === 11) {
    // International format +1
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  } else if (digits.length === 10) {
    // Domestic format
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  return phone;
};

/**
 * Format a US ZIP code
 */
export const formatUSZipCode = (zip: string | null | undefined): string => {
  if (!zip) return '-';
  
  // Remove all spaces
  const clean = zip.replace(/\s/g, '').trim();
  
  // Format ZIP+4 if applicable
  if (/^\d{9}$/.test(clean)) {
    return `${clean.slice(0, 5)}-${clean.slice(5)}`;
  }
  
  return clean;
};

/**
 * Parse a US date string (MM/DD/YYYY) to Date object
 */
export const parseUSDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;
  
  const month = parseInt(parts[0], 10) - 1; // JavaScript months are 0-indexed
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  
  const date = new Date(year, month, day);
  
  // Validate the date is real
  if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
    return null;
  }
  
  return date;
};

/**
 * US date format string for date pickers
 */
export const US_DATE_FORMAT = 'MM/dd/yyyy';

/**
 * US datetime format string
 */
export const US_DATETIME_FORMAT = 'MM/dd/yyyy hh:mm a';

/**
 * US time format string (12-hour)
 */
export const US_TIME_FORMAT = 'hh:mm a';

// ── Legacy aliases for backward compatibility ──
// These map the old UK function names to the new US equivalents
// so consumers don't break until they're updated.
export const formatUKDate = formatUSDate;
export const formatUKDateTime = formatUSDateTime;
export const formatGBP = formatUSD;
export const formatGBPWhole = formatUSDWhole;
export const formatUKNumber = formatUSNumber;
export const formatUKPhone = formatUSPhone;
export const formatUKPostcode = formatUSZipCode;
export const parseUKDate = parseUSDate;
export const UK_DATE_FORMAT = US_DATE_FORMAT;
export const UK_DATETIME_FORMAT = US_DATETIME_FORMAT;
export const UK_TIME_FORMAT = US_TIME_FORMAT;
